import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, auth } from "./api";
import { useAgencySlug } from "./agency";

const SettingsContext = createContext(null);

const DEFAULTS = {
  agency_name: "Travel Shield",
  tagline: "Viajes de Lujo · Protección Total",
  logo_url: "",
  primary_color: "#004AAD",
  secondary_color: "#7C3AED",
  contact_email: "soporte@atiktech.com",
  contact_phone: "+52 800 CHERRY",
  whatsapp: "",
  website: "https://atiktech.com",
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  // La agencia a mostrar: la de la URL (/a/:slug) si existe; si no, la del
  // staff logueado (para que su panel se vea con su propia marca); si no,
  // "default". La agencia de un VIAJERO logueado no cuenta aquí — sus
  // páginas siempre viven bajo /a/:slug, así que ya llega por la URL; sin
  // eso, una sesión de viajero vieja no debe "teñir" p.ej. la pantalla de
  // login de staff con la marca de otra agencia.
  const urlSlug = useAgencySlug();
  const user = auth.current();
  const isStaff = user && ["master", "agency_admin"].includes(user.role);
  // Las puertas de entrada (/login, /admin/login) son neutras: nunca heredan
  // la marca de una sesión de staff que haya quedado guardada en este
  // navegador (p.ej. de probar otra cuenta sin cerrar sesión) — solo el
  // slug de la URL puede definir su marca.
  const loc = useLocation();
  const barePathname = (loc.pathname.match(/^\/a\/[^/]+(\/.*)?$/)?.[1]) || loc.pathname;
  const isGatewayPage = barePathname === "/login" || barePathname === "/admin/login";
  // El master no pertenece a ninguna agencia — cuando ve sus propias
  // pantallas (no las de una agencia específica vía /a/:slug), su marca
  // viene de sus propias preferencias, no de la agencia "default".
  const isMasterOwnView = user?.role === "master" && !urlSlug && !isGatewayPage;
  const slug = urlSlug || (isStaff && !isGatewayPage && !isMasterOwnView ? user.agency_slug : null) || "default";

  const refresh = async () => {
    try {
      const endpoint = isMasterOwnView ? "/master/preferences" : `/agencies/${slug}/settings`;
      const { data } = await api.get(endpoint);
      setSettings({ ...DEFAULTS, ...data });
      // Inject primary color as CSS variable for live theming.
      // --brand-cyan es un nombre histórico (nunca fue cian) — es el color de
      // marca elegido por CADA agencia, no un cian fijo; el default (si la
      // agencia no eligió otro) es el azul de Travel Shield, #004AAD.
      document.documentElement.style.setProperty("--brand-cyan", data.primary_color || DEFAULTS.primary_color);
      document.documentElement.style.setProperty("--brand-secondary", data.secondary_color || DEFAULTS.secondary_color);
      document.title = `${data.agency_name || "Travel Shield"} · Viajes`;
      // PWA instalable con el nombre/ícono de la agencia correcta — no aplica
      // a las pantallas propias del master, que no se instalan como PWA.
      if (!isMasterOwnView) {
        let manifestLink = document.querySelector('link[rel="manifest"]');
        if (!manifestLink) {
          manifestLink = document.createElement("link");
          manifestLink.rel = "manifest";
          document.head.appendChild(manifestLink);
        }
        // Mismo origen que la página (rewrite en firebase.json → Cloud Run):
        // con otro origen Chrome ignora start_url y scope del manifest.
        manifestLink.href = `/pwa/${slug}/manifest.json`;
      }
    } catch {
      // fall back to defaults
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { refresh(); }, [slug, isMasterOwnView]);

  return (
    <SettingsContext.Provider value={{ settings, refresh, loaded }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext) || { settings: DEFAULTS, refresh: () => {}, loaded: true };